/**
 * PURPOSE: Presentación del grafo de fases: cómo se nombra cada partido, cada lado y cada estado
 *   (FTR-0028 WG5, §24–§25).
 * RESPONSIBILITIES: Convertir la respuesta de `GET /graph` y de `POST /graph/recompute` en texto que
 *   un administrador entienda sin haber leído el FTR.
 *
 *   PURO Y SIN REACT, para poder probarlo. Lo que aquí se decide no es estética: es la diferencia
 *   entre "Por definir" (todavía no se sabe, y está bien) y "el equipo que debería estar no está"
 *   (hay que hacer algo). Esos dos estados se ven igual en la base — un `idparticipant` vacío — y
 *   solo el motivo que devuelve el backend los distingue.
 *
 *   EL MOTIVO VIENE DEL SERVIDOR, NO SE ADIVINA AQUÍ. `SOURCE_PENDING` y `GROUP_INCOMPLETE` son
 *   normales a mitad de torneo; `POSITION_AMBIGUOUS` o `PARTICIPANT_OUT_OF_SCOPE` piden intervención.
 *   Traducirlos aquí a partir de datos sueltos daría un mensaje distinto al que el backend conoce.
 * COLLABORATORS: panels/FinalsPanel.jsx, hooks/useProgrammingCenterData.js,
 *   utils/programmingCenterModel.js.
 * ROUTE: winscore/src/utils/programmingCenterGraph.js
 */
import { participantLabel, UNDEFINED_SIDE_LABEL } from "./programmingCenterModel.js";

/** Por qué un lado sigue sin participante. Las claves son las del backend (`PENDING_REASON`). */
export const PENDING_REASON_TEXT = Object.freeze({
  SOURCE_MISSING: "el partido de origen todavía no existe",
  SOURCE_PENDING: "el partido de origen aún no está oficializado",
  SOURCE_UNDECIDED: "el partido de origen no designa un ganador (empate, doble W.O. o descanso)",
  GROUP_INCOMPLETE: "el grupo todavía no ha terminado",
  POSITION_NOT_FOUND: "esa posición aún no existe en la tabla",
  POSITION_AMBIGUOUS: "esa posición la ocupan dos equipos: hay que resolver el empate",
  PARTICIPANT_OUT_OF_SCOPE: "la institución fijada no pertenece a este evento y deporte",
  NOT_AUTOMATIC: "este lado no lo calcula el grafo",
});

/** Por qué el motor no escribió un valor que sí había resuelto. */
export const CONFLICT_REASON_TEXT = Object.freeze({
  MATCH_HAS_ACTIVITY: "el partido ya tiene actividad competitiva",
  CLEAR_BLOCKED_BY_ACTIVITY: "habría que vaciarlo, pero el partido ya tiene actividad",
  DUPLICATE_IN_MATCH: "los dos lados resuelven al mismo equipo",
  GRAPH_CYCLE: "este partido forma parte de un ciclo del grafo",
});

const reasonText = (reason) => PENDING_REASON_TEXT[reason] || CONFLICT_REASON_TEXT[reason] || null;

/**
 * Los seis estados de un lado, con su color y su explicación. El orden de prioridad lo fija el
 * backend (`sideStatus`); aquí solo se le pone nombre.
 */
export const SIDE_STATUS_META = Object.freeze({
  resolved: {
    label: "Resuelto",
    className: "border-emerald-200 bg-emerald-50",
    describe: () => "Definido por el resultado del partido de origen.",
  },
  pending: {
    label: "Por definir",
    className: "border-slate-200 bg-slate-50",
    describe: (side) => {
      const why = reasonText(side?.reason);
      return why ? `Por definir: ${why}.` : "Por definir.";
    },
  },
  stale: {
    label: "Sin aplicar",
    className: "border-sky-200 bg-sky-50",
    describe: () => "Hay un valor nuevo sin aplicar: pulsa «Actualizar clasificados».",
  },
  locked: {
    label: "Bloqueado",
    className: "border-indigo-200 bg-indigo-50",
    describe: () => "Bloqueado a mano: el motor no lo recalcula.",
  },
  conflict: {
    label: "Conflicto",
    className: "border-rose-200 bg-rose-50",
    describe: (side) => {
      const why = reasonText(side?.reason) || "el partido ya tiene actividad competitiva";
      return `No se cambió porque ${why}.`;
    },
  },
  manual: {
    label: "Manual",
    className: "border-slate-200 bg-white",
    describe: () => "Este lado no lo calcula el grafo (slot del sorteo o texto libre).",
  },
});

/** Nombre del partido en el grafo: su etiqueta de fase final, o su clave si no la tiene. */
export const graphNodeTitle = (node) => {
  if (!node) return "";
  const key = String(node.draft_key || node.match_key || "").replace(/^PC-/, "");
  return node.tag_final || key || `Partido ${node.idmatch}`;
};

/**
 * Institución que ocupa hoy ese lado. Si está vacío, se dice "Por definir" — nunca un id técnico ni
 * una cadena vacía, que en pantalla se leerían como un fallo de carga.
 */
export const sideParticipantLabel = (side, index = {}) => {
  const current = Number(side?.current) || null;
  if (!current) return UNDEFINED_SIDE_LABEL;
  const participant = index?.participantsById?.get?.(current) || null;
  return participantLabel(participant) || `Participante ${current}`;
};

/** Una frase con lo que hizo la última recomputación. Sin números sueltos sin contexto. */
export const describeRecompute = (result) => {
  const summary = result?.summary || {};
  const parts = [];
  if (summary.updated) parts.push(`${summary.updated} lados actualizados`);
  if (summary.cleared) parts.push(`${summary.cleared} lados vaciados`);
  if (summary.conflicts) parts.push(`${summary.conflicts} en conflicto`);
  if (summary.pending) parts.push(`${summary.pending} por definir`);
  if (summary.skipped) parts.push(`${summary.skipped} bloqueados o manuales`);
  if (!parts.length) return "El cuadro ya estaba al día: no cambió nada.";
  return parts.join(" · ");
};
